import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  FaLinkedin,
  FaInstagram,
  FaCalendarCheck,
  FaTag,
  FaClock,
  FaStar,
} from "react-icons/fa";
import { useI18n } from "../contexts/I18n";
import { marketingI18n } from "../content/homeMarketing";
import useScrollAnimate from "../hooks/useScrollAnimate";
import MarketingPageIntro from "./marketing/MarketingPageIntro";
import "./Home.css";
import "./MarketingPages.css";
import "./Team.css";

const RAW_TEAM = [
  {
    name: "Youssef Helal",
    role: "CEO and CTO",
    image: "/images/Youssef.JPG",
    linkedin: "https://www.linkedin.com/in/YoussefHelal",
    instagram: "https://www.instagram.com/YoussefHelal",
  },
];

const DEFAULT_AVATAR = "/images/Youssef.JPG";

const BENEFIT_ICONS = [FaCalendarCheck, FaTag, FaClock, FaStar];

export default function Team() {
  const { t, lang } = useI18n();
  const pageT = marketingI18n[lang].pages.team;
  useScrollAnimate([lang]);

  const team = useMemo(() => {
    const map = new Map();
    RAW_TEAM.forEach((m) => map.set(`${m.name}|${m.role}`, m));
    return Array.from(map.values());
  }, []);

  const benefits = [
    t("quickEasyBooking"),
    t("transparentPricing"),
    t("timeSaving"),
    t("ratingsReviews"),
  ];

  const orgLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "ehgezly",
    employee: team.map((m) => ({
      "@type": "Person",
      name: m.name,
      jobTitle: m.role,
      image: m.image,
      sameAs: [m.linkedin, m.instagram].filter(Boolean),
    })),
  };

  const founder = team[0];

  return (
    <div
      className={`marketing-page team-page ${lang === "ar" ? "rtl" : ""}`}
      dir={lang === "ar" ? "rtl" : "ltr"}
    >
      <MarketingPageIntro variant="team" page={pageT} lang={lang} layout="split" />

      <section className="team-founder-section" aria-labelledby="team-founder-heading">
        <div className="container">
          <div className="team-founder-grid scroll-animate from-bottom">
            <article className="team-founder-card">
              <div className="team-founder-card__avatar-wrap">
                <img
                  src={founder.image}
                  alt={`${founder.name}, ${founder.role}`}
                  className="team-founder-card__avatar"
                  onError={(e) => {
                    if (e.currentTarget.dataset.fallback) return;
                    e.currentTarget.dataset.fallback = "1";
                    e.currentTarget.src = DEFAULT_AVATAR;
                  }}
                />
              </div>
              <h2 className="team-founder-card__name">{founder.name}</h2>
              <p className="team-founder-card__role">{founder.role}</p>
              <div className="team-founder-card__social">
                <a
                  href={founder.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  className="team-social-btn"
                >
                  <FaLinkedin />
                </a>
                <a
                  href={founder.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="team-social-btn"
                >
                  <FaInstagram />
                </a>
              </div>
            </article>

            <div className="team-founder-story">
              <h2 id="team-founder-heading" className="team-section-title">
                {t("ourVision")}
              </h2>
              <p className="team-founder-story__text">{t("visionText")}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="team-benefits-section" aria-labelledby="team-benefits-heading">
        <div className="container">
          <h2 id="team-benefits-heading" className="team-section-title team-section-title--center">
            {t("teamBenefitsTitle")}
          </h2>
          <ul className="team-benefits-grid scroll-animate from-bottom delay-1">
            {benefits.map((label, index) => {
              const Icon = BENEFIT_ICONS[index];
              return (
                <li key={label} className="team-benefit-card">
                  <span className="team-benefit-card__icon" aria-hidden="true">
                    <Icon size={22} />
                  </span>
                  <span className="team-benefit-card__label">{label}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      <section className="team-cta-section">
        <div className="container team-cta-inner scroll-animate from-bottom delay-2">
          <h2 className="team-cta-title">{t("teamJoinTitle")}</h2>
          <p className="team-cta-desc">{t("teamJoinDesc")}</p>
          <div className="team-cta-actions">
            <Link to="/support" className="team-cta-btn team-cta-btn--primary">
              {t("teamJoinCta")}
            </Link>
            <Link to="/for-business" className="team-cta-btn team-cta-btn--secondary">
              {lang === "ar" ? "للأعمال" : "For Business"}
            </Link>
          </div>
        </div>
      </section>

      <div className="section-wave section-wave--light" aria-hidden="true" />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgLd) }}
      />
    </div>
  );
}
